import { useTranslation } from 'react-i18next';
import { ArrowRight, Star, Clock, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RemoteImage } from '../../../core/components/RemoteImage';

interface ExpertPickProps {
    config?: any;
}

export const ExpertPick = ({ config = {} }: ExpertPickProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // Extract dynamic config with fallbacks
    const badgeText = config.badge?.[i18n.language] || t('explore.expert.badge', 'Expert Pick');
    const subtitleText = config.subtitle?.[i18n.language] || t('explore.expert.month', 'Deal of the Month');
    // Using main translations for title since it's shared across configs
    const titleText = config.title?.[i18n.language] || t('explore.expert.title', 'Luxury Sea-View Penthouse in Solare');

    const feature1 = config.feature1?.[i18n.language] || t('explore.expert.price', 'Starting from 12M EGP');
    const feature2 = config.feature2?.[i18n.language] || t('explore.expert.limited', 'Limited Time Payment Plan');
    const ctaText = config.ctaText?.[i18n.language] || t('explore.expert.cta', 'Request Floor Plan & Prices');
    const ctaUrl = config.ctaUrl || "/search?ready=true";
    const bgImage = config.backgroundImage || "https://images.unsplash.com/photo-1613490493576-7fde63acd811?q=80&w=2671&auto=format&fit=crop";

    return (
        <section className="py-12 md:py-20 w-full relative overflow-hidden bg-slate-900 group">
            {/* Full Bleed Background Image with subtle zoom on hover */}
            <div className="absolute inset-0 z-0">
                <RemoteImage
                    src={bgImage}
                    alt={titleText}
                    className="w-full h-full object-cover origin-center transition-transform duration-[10s] ease-out group-hover:scale-110 opacity-60 md:opacity-80"
                />
            </div>

            {/* Gradient Overlay for Text Readability */}
            <div className={`absolute inset-0 z-10 bg-gradient-to-t md:bg-gradient-to-r ${isRTL ? 'md:bg-gradient-to-l' : ''} from-slate-900/90 via-slate-900/60 to-transparent`} />

            <div className="relative z-20 max-w-7xl mx-auto px-4 md:px-8 h-full flex items-center">
                <div className={`w-full md:w-1/2 lg:w-5/12 ${isRTL ? 'ml-auto text-right' : 'mr-auto text-left'}`}>

                    {/* Glassmorphic Content Card */}
                    <div className="backdrop-blur-md bg-white/10 border border-white/20 p-8 md:p-12 rounded-3xl shadow-2xl transition-all duration-500 hover:bg-white/15">

                        {/* Header Tags */}
                        <div className={`flex items-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <div className="bg-gradient-to-r from-amber-400 to-amber-600 text-amber-950 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-amber-500/20">
                                <Star className="w-3.5 h-3.5 fill-current" />
                                <span className="tracking-wide uppercase">{badgeText}</span>
                            </div>
                            <span className="text-white/90 text-sm font-medium tracking-wide uppercase">
                                {subtitleText}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-medium text-white mb-6 leading-[1.2] drop-shadow-md">
                            {titleText}
                        </h2>

                        {/* Feature Highlights */}
                        <div className="space-y-4 mb-10">
                            <div className={`flex items-center gap-4 text-white hover:text-amber-300 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/10">
                                    <Wallet className="w-5 h-5 text-amber-400" />
                                </div>
                                <span className="text-base md:text-lg font-medium tracking-wide">{feature1}</span>
                            </div>
                            <div className={`flex items-center gap-4 text-white hover:text-amber-300 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0 border border-white/10">
                                    <Clock className="w-5 h-5 text-amber-400" />
                                </div>
                                <span className="text-base md:text-lg font-medium tracking-wide">{feature2}</span>
                            </div>
                        </div>

                        {/* CTA Action */}
                        <Link
                            to={ctaUrl}
                            className={`group/btn inline-flex items-center justify-center bg-white text-slate-900 px-8 py-4 rounded-xl font-bold text-sm hover:bg-amber-400 hover:text-amber-950 transition-all duration-300 shadow-xl shadow-white/5 hover:shadow-amber-400/20 hover:-translate-y-1 w-full md:w-auto ${isRTL ? 'flex-row-reverse' : ''}`}
                        >
                            <span className="tracking-wider uppercase">{ctaText}</span>
                            <div className="overflow-hidden relative ml-3 mr-0">
                                <ArrowRight className={`transform transition-transform duration-300 group-hover/btn:translate-x-full ${isRTL ? 'rotate-180 mr-3' : 'w-5 h-5'}`} />
                            </div>
                        </Link>
                    </div>

                </div>
            </div>
        </section>
    );
};
